import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GeneralService } from '../../services/general/general.service';
import { DateRange } from '../../interfaces/date-range.interface';
import { EventApi } from '@fullcalendar/common';
import { PaymentDialogComponent } from 'src/app/components/payment-dialog/payment-dialog.component';
import { BookingDialogComponent } from 'src/app/components/booking-dialog/booking-dialog.component';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { DateTime } from 'luxon';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

@Component({
  selector: 'app-rescheedule-dialog',
  templateUrl: './rescheedule-dialog.component.html',
  styleUrls: ['./rescheedule-dialog.component.scss'],
})
export class RescheeduleDialogComponent implements OnInit {
  public teacher: any = {};
  public timezone: any;
  public loading = false;
  public teacherId: Number | undefined;
  public schedule: any = new Array();
  public form: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<RescheeduleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private route: ActivatedRoute,
    private general: GeneralService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder
  ) {
    this.form = this.formBuilder.group({
      lesson: [null],
    });
    this.teacherId = this.data.teacherId;
  }

  async ngOnInit() {
    try {
      this.loading = true;
      await this.getTeacher();
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Get data from current teacher
   */
  async getTeacher() {
    const params = { other_user_id: this.teacherId };

    const requests = [
      this.general.httpGet(`teacher/${this.teacherId}`),
      this.general.httpGet('booking-mine', params),
    ];

    const response: any = await this.general.forkJoin(requests);

    this.teacher = response[0].data;

    this.teacher.custom_lessons_modified = this.general.getArrayUniqueValues(
      this.teacher.custom_lessons,
      ['name', 'lesson_id']
    );

    this.teacher.lessons = this.teacher.lessons.map((e: any) => {
      e.type = e?.levels[0]?.type;
      return e;
    });
  }

  /**
   * Get shedule from api
   * @param dateRange
   * @returns
   */
  async getSchedule(dateRange: DateRange) {
    this.timezone = dateRange.timezone;
    let request = await this.general.httpGet(
      `booking-schedule/${this.teacherId}`,
      {
        start_date: dateRange.start,
        end_date: dateRange.end,
        timezone: this.timezone,
      }
    );

    let schedule = request.data.map((element: any) => {
      return {
        ...element,
        groupId: this.general.getRandomId(10),
        selected: false,
      };
    });

    return this.concatenateConsecutiveEvents(schedule);
  }
  /**
   * Lister to be triggered when the full calendar's view change
   * @param event
   */
  async onChangeView(event: DateRange) {
    const schedule: any = await this.getSchedule(event);
    this.schedule = [].concat(schedule);
  }

  /**
   * Event listener to tigger on event is clicked
   * @param event
   */
  async onEventClicked(event: any) {
    try {
      const index = this.schedule.findIndex((e: any) => {
        let start = DateTime.fromISO(event.startStr).setZone('UTC').toISO();
        let eventStart = DateTime.fromISO(e.start).setZone('UTC').toISO();
        return eventStart === start;
      });
      this.checkEvent(index);
    } catch (error: any) {
      console.log(error);
    }
  }

  /**
   * If event clicked is already selected, init process of concatenated
   * Else mark it as selected or show Booking dialog
   * @param index
   * @returns
   */
  checkEvent(index: any) {
    const selecteds = this.schedule.filter((e: any) => {
      return e.selected;
    });
    if (
      !this.schedule[index].selected &&
      selecteds.length === this.data.maxHours
    ) {
      return;
    }

    if (this.schedule[index].selected) {
      this.schedule[index].selected = !this.schedule[index].selected;
      this.schedule = this.concatenateConsecutiveEvents(this.schedule);
      return;
    }

    let start = DateTime.fromISO(this.schedule[index].start).setZone('UTC');
    let end = DateTime.fromISO(this.schedule[index].end).setZone('UTC');
    let diff = end.diff(start, 'minutes').minutes;
    if (diff > 60) {
      this.showBookingDialog({ start, end, timezone: this.timezone }, index);
    } else {
      this.schedule[index].selected = !this.schedule[index].selected;
      this.schedule = [].concat(this.schedule);
    }
  }

  /**
   * Show Dialog to select start of range to create a class
   * @param data
   * @param index
   */
  showBookingDialog(data: Object, index: any) {
    const bookingDialogRef = this.dialog.open(BookingDialogComponent, {
      data,
      autoFocus: false,
    });

    const subscribed = bookingDialogRef
      .afterClosed()
      .subscribe(async (data) => {
        subscribed.unsubscribe();
        if (!data) {
          return;
        }
        this.configureSchedule(data, index);
      });
  }

  /**
   * Adapt current schedule to be understable for the user
   * @param data
   * @param index
   */
  configureSchedule(data: any, index: any) {
    const schedules = [];
    const selectedStart: DateTime = data.start;
    const selectedEnd: DateTime = data.end;
    let start = DateTime.fromISO(this.schedule[index].start).setZone('UTC');
    let end = DateTime.fromISO(this.schedule[index].end).setZone('UTC');
    let diffStarts = selectedStart.diff(start, 'minutes').minutes;
    let diffEnds = end.diff(selectedEnd, 'minutes').minutes;

    if (diffStarts) {
      schedules.push({
        start: start.toISO(),
        end: selectedStart.toISO(),
        available: diffStarts >= 59,
        selected: false,
        groupId: this.schedule[index].groupId,
      });
    }

    if (diffEnds) {
      schedules.push({
        start: selectedEnd.toISO(),
        end: end.toISO(),
        available: diffEnds >= 59,
        selected: false,
        groupId: this.schedule[index].groupId,
      });
    }

    schedules.push({
      start: selectedStart.toISO(),
      end: selectedEnd.toISO(),
      available: true,
      selected: true,
      groupId: this.schedule[index].groupId,
    });

    this.schedule.splice(index, 1);

    this.schedule = this.schedule.concat(schedules);
  }

  /**
   * Join events which are consecutives and availables
   * @param entry
   * @returns
   */
  concatenateConsecutiveEvents(entry: any) {
    let availableEvents: any = [];
    let otherEvents: any = [];

    entry.forEach((e: any) => {
      if (!e.selected) {
        availableEvents.push(e);
      } else {
        otherEvents.push(e);
      }
    });

    let exit: any = [];

    availableEvents.sort((a: any, b: any) => {
      const startA = DateTime.fromISO(a.start).setZone('UTC');
      const startB = DateTime.fromISO(b.start).setZone('UTC');

      if (startA.toMillis() > startB.toMillis()) {
        return 1;
      }
      return -1;
    });

    for (let index = 0; index < availableEvents.length; index++) {
      if (index === 0) {
        exit.push(availableEvents[index]);
        continue;
      }
      const currentEvent = availableEvents[index];
      const previousEvent = availableEvents[index - 1];

      const currentEventStart = DateTime.fromISO(currentEvent.start).setZone(
        'UTC'
      );
      const previousEventEnd = DateTime.fromISO(previousEvent.end).setZone(
        'UTC'
      );
      if (!currentEventStart.equals(previousEventEnd)) {
        exit.push(currentEvent);
        continue;
      }

      exit[exit.length - 1].end = availableEvents[index].end;

      if (currentEvent.available && !exit[exit.length - 1].available) {
        exit[exit.length - 1].available = true;
      }
    }
    exit = exit.concat(otherEvents);

    return exit;
  }

  closeDialog() {
    this.dialogRef.close();
  }

  /**
   * Chekc if booking button is disabled
   */
  get isDisabled() {
    const selecteds = this.schedule.filter((e: any) => {
      return e.selected;
    });
    if (
      selecteds.length > 0 &&
      selecteds.length <= this.data.maxHours &&
      this.form.valid
    )
      return false;
    return true;
  }

  /**
   * Used for reschedule bookings
   */
  async onReschedule() {
    try {
      const selected = this.schedule.filter((e: any) => {
        return e.selected;
      })[0];

      if (!selected) {
        return;
      }
      const res = {
        booking_start_datetime: selected.start,
        booking_end_datetime: selected.end,
      };
      this.dialogRef.close(res);
    } catch (error) {
      console.log(error);
    } finally {
    }
  }
}
